/**
 * Insufficient SOL Guard
 *
 * Shared utility for detecting and displaying user-friendly notifications
 * when a wallet lacks SOL for Solana network fees. Two layers:
 *   1. Pre-flight guard: check balance before submitting a transaction
 *   2. Error detection: catch "insufficient funds" errors from RPC/simulation
 */

import { toast } from 'sonner';

/** Minimum SOL balance required to cover network fees for any transaction */
export const MIN_SOL_FOR_FEES = 0.005;

const INSUFFICIENT_SOL_TITLE = 'Not enough SOL for network fees';

function depositSolAction() {
  return {
    label: 'Deposit SOL',
    onClick: () => {
      window.dispatchEvent(new CustomEvent('memelaunch:open-wallet-deposit'));
    },
  };
}

/**
 * Pre-flight check: returns true if the wallet has enough SOL for fees.
 */
export function hasSufficientSolForFees(balance: number | null): boolean {
  if (balance === null) return false;
  return balance >= MIN_SOL_FOR_FEES;
}

/**
 * Detect if an error is an insufficient-SOL error from the RPC or simulation.
 */
export function isInsufficientSolError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  return (
    lower.includes('insufficient funds') ||
    lower.includes('insufficient lamports') ||
    lower.includes('attempt to debit an account but found no record')
  );
}

/**
 * Show the standard insufficient-SOL toast notification.
 */
export function showInsufficientSolToast(): true {
  toast.error(INSUFFICIENT_SOL_TITLE, {
    description:
      'Every transaction on Solana requires a small amount of SOL to process. Deposit SOL to your wallet and try again.',
    duration: 8000,
    action: depositSolAction(),
  });
  return true;
}

/**
 * Show an insufficient-SOL toast with partial completion progress.
 * Used when a batch operation partially succeeds before running out of SOL.
 *
 * @param completed - number of items that succeeded
 * @param total - total items attempted
 * @param noun - what was being processed (e.g. "pools", "accounts")
 * @param verb - past tense action (e.g. "Claimed", "Closed")
 */
export function showInsufficientSolToastWithProgress(
  completed: number,
  total: number,
  noun: string,
  verb: string,
): true {
  const description =
    completed > 0
      ? `${verb} ${completed} of ${total} ${noun}. Deposit more SOL to continue.`
      : `Every transaction on Solana requires a small amount of SOL to process. Deposit SOL to your wallet and try again.`;

  toast.error(INSUFFICIENT_SOL_TITLE, {
    description,
    duration: 8000,
    action: depositSolAction(),
  });
  return true;
}

/**
 * Combined guard: checks balance and shows toast if insufficient.
 * Returns true if the transaction should be BLOCKED (insufficient SOL).
 */
export function guardInsufficientSol(balance: number | null): boolean {
  if (!hasSufficientSolForFees(balance)) {
    showInsufficientSolToast();
    return true;
  }
  return false;
}

/**
 * Error handler: checks if an error is insufficient-SOL and shows toast.
 * Returns true if it was handled (so callers can skip generic error display).
 */
export function handleInsufficientSolError(error: unknown): boolean {
  if (isInsufficientSolError(error)) {
    showInsufficientSolToast();
    return true;
  }
  return false;
}
